import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Grid,
  Paper,
  Alert,
  Box,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import { useTheme, createTheme, ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";

import ProductSearchPanel from "./ProductSearchPanel";
import CartTabs from "./CartTabs";
import TotalsFooter from "./TotalsFooter";
import VariantPickerDialog from "./VariantPickerDialog";

import { getProducts } from "../../api/products";
import { createPosOrder } from "../../api/order";

const APPBAR_H = 64;

const Sales = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isDialogFullScreen = isSmDown;

  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);

  const compactTheme = useMemo(
    () =>
      createTheme(theme, {
        typography: {
          ...theme.typography,
          fontSize: 12,
          h6: { ...theme.typography.h6, fontSize: "1rem", fontWeight: 900 },
          subtitle1: { ...theme.typography.subtitle1, fontSize: "0.95rem", fontWeight: 800 },
          body1: { ...theme.typography.body1, fontSize: "0.9rem" },
          body2: { ...theme.typography.body2, fontSize: "0.82rem" },
        },
        components: {
          MuiButton: {
            defaultProps: { size: "small" },
            styleOverrides: {
              root: { padding: "7px 12px", borderRadius: 10, textTransform: "none", fontWeight: 800 },
            },
          },
          MuiTextField: { defaultProps: { size: "small" } },
          MuiFormControl: { defaultProps: { size: "small" } },
          MuiSelect: { defaultProps: { size: "small" } },
          MuiIconButton: { defaultProps: { size: "small" } },
          MuiChip: { defaultProps: { size: "small" } },
          MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
        },
      }),
    [theme]
  );

  // ---- State ----
  const [customer, setCustomer] = useState({
    userId: null,
    name: "",
    phone: "",
    email: "",
    address: "",
    loyaltyPoints: 0,
  });

  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [note, setNote] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("amount");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const shippingFee = 35000;

  // Tổng tiền
  const subtotal = products.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discountValue = discountType === "percent" ? (subtotal * discount) / 100 : discount;
  const finalTotal = subtotal - discountValue + (shippingEnabled ? shippingFee : 0);

  // Gọi API khi mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getProducts();
        const mapped = (data || []).map((p) => ({
          id: p.productId,
          name: p.name,
          price: p.finalPrice ?? p.price ?? 0,
          stock: p.totalStock ?? 0,
          sku: p.sku,
          image: p.imageUrls?.[0] || "/api/placeholder/60/60",
          variations: (p.variations || []).map((v) => ({
            color: v.color,
            sizes: (v.sizes || []).map((s) => ({
              size: s.size,
              stock: s.stock,
              price: s.price ?? p.finalPrice ?? p.price ?? 0,
              status: s.status,
              variantId: s.variantId,
            })),
          })),
        }));
        if (alive) {
          setAllProducts(mapped);
          setError("");
        }
      } catch (e) {
        if (alive) setError("Không tải được danh sách sản phẩm");
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Debounce search
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const filteredProducts = (loading || error ? [] : allProducts).filter(
    (p) =>
      p.name.toLowerCase().includes(debouncedTerm.toLowerCase()) ||
      (p.sku || "").toLowerCase().includes(debouncedTerm.toLowerCase())
  );

  // ----- Chọn biến thể -----
  const [variantOpen, setVariantOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleSelectProduct = useCallback((product) => {
    setSelectedProduct(product);
    setVariantOpen(true);
  }, []);

  const handleConfirmVariant = useCallback((variant) => {
    setVariantOpen(false);
    if (!selectedProduct) return;

    const key = `${selectedProduct.id}__${variant.color}__${variant.size}`;
    const existing = products.find((p) => p.key === key);

    const qtyFromDialog = Math.min(Math.max(variant.quantity || 1, 1), variant.stock || 1);

    if (existing) {
      const nextQty = Math.min(existing.quantity + qtyFromDialog, variant.stock);
      setProducts(products.map((p) => (p.key === key ? { ...p, quantity: nextQty } : p)));
    } else {
      setProducts([
        ...products,
        {
          key,
          id: selectedProduct.id,
          name: selectedProduct.name,
          sku: selectedProduct.sku,
          image: selectedProduct.image,
          color: variant.color,
          size: variant.size,
          variantId: variant.variantId,
          price: Number.isFinite(Number(variant.price)) ? Number(variant.price) : Number(selectedProduct.finalPrice ?? selectedProduct.price ?? 0),
          quantity: qtyFromDialog,
          stock: variant.stock,
        },
      ]);
    }
    setSelectedProduct(null);
  }, [products, selectedProduct]);

  const handleQuantityChange = useCallback((key, newQuantity) => {
    const item = products.find((p) => p.key === key);
    if (!item) return;

    if (newQuantity < 1) {
      setProducts(products.filter((p) => p.key !== key));
    } else if (newQuantity <= item.stock) {
      setProducts(products.map((p) => (p.key === key ? { ...p, quantity: newQuantity } : p)));
    }
  }, [products]);

  // Thanh toán
  const handleCheckout = useCallback(async () => {
    if (!products.length) return;
    try {
      setSubmitting(true);
      const payload = {
        userId: customer.userId || null,
        note,
        paymentMethod,
        discount: { type: discountType, value: Number(discount) || 0 },
        shippingFee: shippingEnabled ? shippingFee : 0,
        items: products.map((p) => ({
          productId: p.id,
          variantId: p.variantId,
          color: p.color,
          size: p.size,
          quantity: p.quantity,
        })),
        customer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
        },
      };
      await createPosOrder(payload);

      setPaymentSuccess(true);
      setProducts([]);
      setCustomer({ userId: null, name: "", phone: "", email: "", address: "", loyaltyPoints: 0 });
      setNote("");
      setDiscount(0);
      setDiscountType("amount");
      setShippingEnabled(false);
      setTabValue(0);
    } catch (e) {
      console.error(e);
      alert("Tạo đơn thất bại. Vui lòng thử lại!");
    } finally {
      setSubmitting(false);
    }
  }, [customer, note, paymentMethod, discountType, discount, shippingEnabled, products]);

  // Auto-hide alert
  useEffect(() => {
    if (!paymentSuccess) return;
    const timer = setTimeout(() => setPaymentSuccess(false), 3000);
    return () => clearTimeout(timer);
  }, [paymentSuccess]);

  const cartCount = products.reduce((n, p) => n + (p.quantity || 0), 0);

  const topBarSx = {
    px: isMdDown ? 1.5 : 2,
    py: 1.25,
    borderRadius: 2.5,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: { xs: "stretch", md: "center" },
    justifyContent: "space-between",
    flexDirection: { xs: "column", md: "row" },
    gap: 1,
    mb: 2,
  };

  const panelSx = {
    p: isMdDown ? 1.5 : 2,
    borderRadius: 2.5,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  };

  return (
    <ThemeProvider theme={compactTheme}>
      <Container
        maxWidth="xl"
        sx={{
          py: 2,
          height: { xs: "auto", md: `calc(100vh - ${APPBAR_H}px)` },
          display: "flex",
          flexDirection: "column",
          pb: { xs: "140px", md: 2 },
        }}
      >
        {/* TOP BAR */}
        <Paper elevation={0} sx={topBarSx}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">POS / Bán hàng</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Tìm sản phẩm → chọn biến thể → thêm vào giỏ → thanh toán
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
            <Chip label={`Món: ${products.length}`} variant="outlined" />
            <Chip label={`SL: ${cartCount}`} variant="outlined" />
            <Chip label={`Tổng: ${finalTotal.toLocaleString("vi-VN")}₫`} color={products.length ? "primary" : "default"} />
            {shippingEnabled && <Chip label={`Ship: ${shippingFee.toLocaleString("vi-VN")}₫`} variant="outlined" />}
          </Box>
        </Paper>

        {paymentSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thanh toán thành công! Đơn hàng trị giá {finalTotal.toLocaleString("vi-VN")}₫ đã được tạo.
          </Alert>
        )}

        <Grid container spacing={isMdDown ? 2 : 3} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* LEFT */}
          <Grid item xs={12} md={4} sx={{ height: { xs: "auto", md: "100%" } }}>
            <Paper elevation={0} sx={panelSx}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Sản phẩm</Typography>
                <Typography variant="body2" color="text.secondary">
                  Tìm theo tên/SKU, bấm để chọn biến thể
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />

              <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <ProductSearchPanel
                  t={t}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  filteredProducts={filteredProducts}
                  onSelectProduct={handleSelectProduct}
                  loading={loading}
                  error={error}
                />
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT */}
          <Grid item xs={12} md={8} sx={{ height: { xs: "auto", md: "100%" } }}>
            <Paper elevation={0} sx={panelSx}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1">Giỏ hàng</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Điều chỉnh số lượng, ghi chú, chọn phương thức thanh toán
                  </Typography>
                </Box>
                <Chip
                  label={products.length ? `Đang có ${cartCount} sản phẩm` : "Giỏ trống"}
                  color={products.length ? "primary" : "default"}
                  variant={products.length ? "filled" : "outlined"}
                />
              </Box>
              <Divider sx={{ mb: 1 }} />

              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
                <CartTabs
                  tabValue={tabValue}
                  setTabValue={setTabValue}
                  products={products}
                  customer={customer}
                  setCustomer={setCustomer}
                  note={note}
                  setNote={setNote}
                  subtotal={subtotal}
                  handleQuantityChange={handleQuantityChange}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                />
              </Box>

              {/* Desktop sticky footer */}
              {!isMdDown && (
                <Box
                  sx={{
                    mt: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    pt: 1.5,
                    bgcolor: "background.paper",
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                  }}
                >
                  <TotalsFooter
                    subtotal={subtotal}
                    discount={discount}
                    discountType={discountType}
                    discountValue={discountValue}
                    finalTotal={finalTotal}
                    setDiscount={setDiscount}
                    setDiscountType={setDiscountType}
                    onCheckout={handleCheckout}
                    canCheckout={products.length > 0 && !submitting}
                    shippingEnabled={shippingEnabled}
                    setShippingEnabled={setShippingEnabled}
                    shippingFee={shippingFee}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        <VariantPickerDialog
          open={variantOpen}
          onClose={() => setVariantOpen(false)}
          product={selectedProduct}
          onConfirm={handleConfirmVariant}
          fullScreen={isDialogFullScreen}
        />

        {/* Mobile fixed footer */}
        {isMdDown && (
          <Box
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              bgcolor: "background.paper",
              borderTop: `1px solid ${theme.palette.divider}`,
              boxShadow: 6,
              px: 1.5,
              py: 1,
              pb: `calc(10px + env(safe-area-inset-bottom))`,
            }}
          >
            <TotalsFooter
              subtotal={subtotal}
              discount={discount}
              discountType={discountType}
              discountValue={discountValue}
              finalTotal={finalTotal}
              setDiscount={setDiscount}
              setDiscountType={setDiscountType}
              onCheckout={handleCheckout}
              canCheckout={products.length > 0 && !submitting}
              shippingEnabled={shippingEnabled}
              setShippingEnabled={setShippingEnabled}
              shippingFee={shippingFee}
            />
          </Box>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default Sales;
